// Centralized sauce data to ensure consistency across all components
// This is the single source of truth for all sauce information

export interface SauceData {
  id: string;
  name: string;
  description: string;
  price: number;
  heat: 'Mild' | 'Medium' | 'Hot' | 'Extreme';
  category: string;
  image: string;
  rating: number;
  reviews: number;
  color: string; // Tailwind gradient classes
  flavorProfile: string; // Detailed flavor description
  ingredients: string; // Key ingredients
  pairings: string; // Food pairings
}

// MASTER SAUCE DATA - Updated with new 6 sauce varieties
export const MASTER_SAUCES: SauceData[] = [
  {
    id: 'yuzu-sesame-zing',
    name: 'Yuzu-Sesame Zing',
    description: 'Citrus crunch, instant lift',
    flavorProfile: 'Japanese yuzu juice, toasted sesame, hint of white miso; thin vinaigrette with micro-sesame crunch',
    ingredients: 'Yuzu juice, toasted sesame, white miso',
    pairings: 'Poke bowls, soba salads, gyoza dip',
    price: 2.99,
    heat: 'Mild',
    category: 'citrus',
    image: '/photoshoot/undressing shoot 1.png',
    rating: 4.8,
    reviews: 142,
    color: 'from-yellow-400 to-orange-500'
  },
  {
    id: 'thai-coco-chili-crush',
    name: 'Thai Coco-Chili Crush',
    description: 'Creamy heat, island beat',
    flavorProfile: 'Coconut cream, lemongrass, bird\'s-eye chili, palm sugar; silky emulsion',
    ingredients: 'Coconut cream, lemongrass, bird\'s-eye chili, palm sugar',
    pairings: 'Charcoal chicken, satay skewers, quinoa bowls',
    price: 2.99,
    heat: 'Medium',
    category: 'tropical',
    image: '/photoshoot/undressing shoot 2.png',
    rating: 4.7,
    reviews: 128,
    color: 'from-green-400 to-emerald-500'
  },
  {
    id: 'mango-ginger-blaze',
    name: 'Mango-Ginger Blaze',
    description: 'Tropical sweet-heat',
    flavorProfile: 'Ripe mango purée, fresh ginger, habanero, rice vinegar; pulpy medium body',
    ingredients: 'Mango purée, fresh ginger, habanero, rice vinegar',
    pairings: 'Shrimp tacos, mixed-green salads, summer rolls',
    price: 2.99,
    heat: 'Hot',
    category: 'tropical',
    image: '/photoshoot/undressing guy 1.png',
    rating: 4.6,
    reviews: 156,
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'black-garlic-umami-drip',
    name: 'Black Garlic Umami Drip',
    description: 'Deep, dark, addictive',
    flavorProfile: 'Fermented black garlic, mushroom soy, touch of maple, smoked paprika; glossy glaze',
    ingredients: 'Black garlic, mushroom soy, maple, smoked paprika',
    pairings: 'Rib-eye, roasted mushrooms, kale Caesar',
    price: 2.99,
    heat: 'Medium',
    category: 'umami',
    image: '/photoshoot/undressing guy 3.png',
    rating: 4.9,
    reviews: 203,
    color: 'from-gray-700 to-gray-900'
  },
  {
    id: 'avocado-cilantro-cream',
    name: 'Avocado-Cilantro Cream',
    description: 'Green, clean, serene',
    flavorProfile: 'Avocado, cilantro, lime, jalapeño whisper, aquafaba for a mayo-free cream',
    ingredients: 'Avocado, cilantro, lime, jalapeño, aquafaba',
    pairings: 'Grain bowls, burritos, crudité dip',
    price: 2.99,
    heat: 'Mild',
    category: 'herb',
    image: '/photoshoot/undressing shoot 4.png',
    rating: 4.5,
    reviews: 89,
    color: 'from-green-500 to-emerald-600'
  },
  {
    id: 'szechuan-peppercorn-spark',
    name: 'Szechuan Peppercorn Spark',
    description: 'Tingle, tingle, boom',
    flavorProfile: 'Mala peppercorn oil, black vinegar, honey, roasted sesame; thin-medium oil',
    ingredients: 'Mala peppercorn oil, black vinegar, honey, sesame',
    pairings: 'Cold noodles, dumplings, smashed cucumbers',
    price: 2.99,
    heat: 'Extreme',
    category: 'spicy',
    image: '/photoshoot/undressing sampler box.png',
    rating: 4.8,
    reviews: 174,
    color: 'from-red-600 to-red-800'
  }
];

// Sampler pack configuration with SGD pricing
export const SAMPLER_PACK = {
  id: 'sampler-6',
  name: '6-Sauce Global Fusion Sampler',
  description: 'Discover bold flavors from around the world with our curated selection of 6 signature international sauces',
  price: 16.00,
  originalPrice: 17.94, // 6 × 2.99 = 17.94
  savings: 1.94, // 17.94 - 16.00 = 1.94
  image: '/photoshoot/undressing sampler box.png',
  includes: MASTER_SAUCES.map(sauce => sauce.name) // Automatically includes all sauce names
};

// Updated flavor categories for filtering
export const SAUCE_CATEGORIES = [
  { id: 'all', name: 'All Sauces', count: MASTER_SAUCES.length },
  { id: 'citrus', name: 'Citrus & Bright', count: MASTER_SAUCES.filter(s => s.category === 'citrus').length },
  { id: 'tropical', name: 'Tropical Heat', count: MASTER_SAUCES.filter(s => s.category === 'tropical').length },
  { id: 'umami', name: 'Umami Rich', count: MASTER_SAUCES.filter(s => s.category === 'umami').length },
  { id: 'herb', name: 'Herb & Green', count: MASTER_SAUCES.filter(s => s.category === 'herb').length },
  { id: 'spicy', name: 'Spicy & Bold', count: MASTER_SAUCES.filter(s => s.category === 'spicy').length }
];

// Utility functions with guardrails
export const sauceUtils = {
  // Get all sauces
  getAllSauces: (): SauceData[] => {
    return [...MASTER_SAUCES]; // Return a copy to prevent mutations
  },

  // Get sauce by ID with validation
  getSauceById: (id: string): SauceData | null => {
    const sauce = MASTER_SAUCES.find(s => s.id === id);
    if (!sauce) {
      console.warn(`Sauce with ID "${id}" not found in MASTER_SAUCES`);
      return null;
    }
    return { ...sauce }; // Return a copy
  },

  // Get sauces by category with validation
  getSaucesByCategory: (category: string): SauceData[] => {
    if (category === 'all') {
      return [...MASTER_SAUCES];
    }
    
    const filteredSauces = MASTER_SAUCES.filter(s => s.category === category);
    if (filteredSauces.length === 0) {
      console.warn(`No sauces found for category "${category}"`);
    }
    
    return filteredSauces;
  },

  // Get flavor flight data (for homepage display)
  getFlavorFlightData: () => {
    return MASTER_SAUCES.map(sauce => ({
      name: sauce.name,
      color: sauce.color,
      description: sauce.description
    }));
  },

  // Validate sauce data integrity
  validateSauceData: (): boolean => {
    const errors: string[] = [];
    
    // Check for duplicate IDs
    const ids = MASTER_SAUCES.map(s => s.id);
    const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      errors.push(`Duplicate sauce IDs found: ${duplicateIds.join(', ')}`);
    }
    
    // Check for missing required fields
    MASTER_SAUCES.forEach((sauce, index) => {
      if (!sauce.id) errors.push(`Sauce at index ${index} missing ID`);
      if (!sauce.name) errors.push(`Sauce at index ${index} missing name`);
      if (!sauce.description) errors.push(`Sauce at index ${index} missing description`);
      if (!sauce.flavorProfile) errors.push(`Sauce at index ${index} missing flavorProfile`);
      if (!sauce.color) errors.push(`Sauce at index ${index} missing color`);
      if (sauce.price <= 0) errors.push(`Sauce "${sauce.name}" has invalid price`);
    });
    
    // Check sampler pack consistency
    const samplerIncludes = SAMPLER_PACK.includes;
    const masterNames = MASTER_SAUCES.map(s => s.name);
    const missingInSampler = masterNames.filter(name => !samplerIncludes.includes(name));
    const extraInSampler = samplerIncludes.filter(name => !masterNames.includes(name));
    
    if (missingInSampler.length > 0) {
      errors.push(`Sampler pack missing sauces: ${missingInSampler.join(', ')}`);
    }
    if (extraInSampler.length > 0) {
      errors.push(`Sampler pack has unknown sauces: ${extraInSampler.join(', ')}`);
    }
    
    if (errors.length > 0) {
      console.error('Sauce data validation errors:', errors);
      return false;
    }
    
    console.log('✅ Sauce data validation passed');
    return true;
  }
};

// Run validation on module load (development guardrail)
if (process.env.NODE_ENV === 'development') {
  sauceUtils.validateSauceData();
}